import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { LibraryAPI } from "@/lib/sprint9-api";
import { InstitutionsAPI } from "@/lib/sprint2-api";
import { StudentsAPI } from "@/lib/sprint3-api";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Library as LibraryIcon, Plus, Loader2, Search, BookOpen, RotateCcw,
  CircleAlert, Settings as SettingsIcon, Trash2, CheckCircle2, DollarSign,
  LayoutDashboard,
} from "lucide-react";
import LibraryDashboard from "@/components/library/LibraryDashboard";

const ISSUE_ROLES = new Set(["librarian", "hod", "principal", "inst_admin", "super_admin"]);

const StaffAPI = {
  list: (params) => api.get("/staff", { params }).then((r) => r.data),
};

export default function Library() {
  const { user } = useAuth();
  const canIssue = (user?.roles || []).some((r) => ISSUE_ROLES.has(r)) && !!user?.tenant_id;
  const [tab, setTab] = useState("dashboard");

  const instQ = useQuery({
    queryKey: ["lib-institutions", user?.tenant_id],
    queryFn: () => InstitutionsAPI.list(),
    enabled: !!user?.tenant_id,
  });
  const [institutionId, setInstitutionId] = useState("");
  const effectiveInst = institutionId || instQ.data?.[0]?.id || "";

  return (
    <AppShell>
      <div className="p-8" data-testid="library-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl">Library</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Catalogue + issue/return + late fines. Per-copy barcodes, student browse and reservations
              are on the backlog.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {instQ.data && instQ.data.length > 1 && (
              <Select value={effectiveInst} onValueChange={setInstitutionId}>
                <SelectTrigger className="w-64" data-testid="lib-inst-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {instQ.data.map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {canIssue && effectiveInst && <SettingsDialog institutionId={effectiveInst} />}
            {canIssue && effectiveInst && <AddBookDialog institutionId={effectiveInst} />}
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList data-testid="library-tabs">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="catalogue" data-testid="tab-catalogue">
              <BookOpen className="h-4 w-4 mr-1.5" /> Catalogue
            </TabsTrigger>
            <TabsTrigger value="loans" data-testid="tab-loans">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Loans
            </TabsTrigger>
            <TabsTrigger value="overdue" data-testid="tab-overdue">
              <CircleAlert className="h-4 w-4 mr-1.5" /> Overdue
            </TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-0">
            <LibraryDashboard />
          </TabsContent>
          <TabsContent value="catalogue" className="mt-0">
            <CataloguePane institutionId={effectiveInst} canIssue={canIssue} />
          </TabsContent>
          <TabsContent value="loans" className="mt-0">
            <LoansPane status="active" canIssue={canIssue} />
          </TabsContent>
          <TabsContent value="overdue" className="mt-0">
            <LoansPane status="overdue" canIssue={canIssue} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

function CataloguePane({ institutionId, canIssue }) {
  const [q, setQ] = useState("");
  const booksQ = useQuery({
    queryKey: ["lib-books", institutionId, q],
    queryFn: () => LibraryAPI.books({ institution_id: institutionId || undefined, q: q || undefined }),
    enabled: !!institutionId,
  });

  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by title / author / ISBN / category…"
          className="pl-8"
          data-testid="catalogue-search"
        />
      </div>
      {booksQ.isLoading ? (
        <div className="bg-card rounded-2xl shadow-sm p-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (booksQ.data || []).length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground" data-testid="catalogue-empty">
          <LibraryIcon className="h-10 w-10 mx-auto opacity-40 mb-3" />
          <div className="text-sm">No books in the catalogue yet.</div>
        </div>
      ) : (
        <div className="bg-card rounded-2xl shadow-sm divide-y divide-border overflow-hidden" data-testid="catalogue-list">
          {(booksQ.data || []).map((b) => (
            <BookRow key={b.id} book={b} canIssue={canIssue} />
          ))}
        </div>
      )}
    </div>
  );
}

function BookRow({ book, canIssue }) {
  const qc = useQueryClient();
  const [openIssue, setOpenIssue] = useState(false);
  const available = book.available_copies;
  const remove = async () => {
    if (!window.confirm(`Remove "${book.title}" from catalogue?`)) return;
    try {
      await LibraryAPI.removeBook(book.id);
      toast.success("Book removed");
      qc.invalidateQueries({ queryKey: ["lib-books"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`book-row-${book.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{book.title}</div>
        <div className="text-xs text-muted-foreground truncate">
          {book.author || "—"} {book.isbn ? `· ISBN ${book.isbn}` : ""} {book.category ? `· ${book.category}` : ""}
        </div>
      </div>
      <Badge variant={available > 0 ? "outline" : "secondary"} className="font-mono text-[10px]">
        {available}/{book.total_copies} available
      </Badge>
      {canIssue && (
        <>
          <Button
            size="sm"
            variant={available > 0 ? "default" : "outline"}
            disabled={available <= 0}
            onClick={() => setOpenIssue(true)}
            data-testid={`book-issue-${book.id}`}
          >
            Issue
          </Button>
          <Button size="icon" variant="ghost" onClick={remove} title="Delete" data-testid={`book-delete-${book.id}`}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
          {openIssue && (
            <IssueDialog book={book} onClose={() => setOpenIssue(false)} />
          )}
        </>
      )}
    </div>
  );
}

function LoansPane({ status, canIssue }) {
  const loansQ = useQuery({
    queryKey: ["lib-loans", status],
    queryFn: () => LibraryAPI.loans({ status }),
  });

  return (
    <div className="space-y-3">
      {loansQ.isLoading ? (
        <div className="bg-card rounded-2xl shadow-sm p-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (loansQ.data || []).length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          <BookOpen className="h-10 w-10 mx-auto opacity-40 mb-3" />
          <div className="text-sm">No {status === "overdue" ? "overdue" : "active"} loans.</div>
        </div>
      ) : (
        <div className="bg-card rounded-2xl shadow-sm divide-y divide-border overflow-hidden" data-testid="loans-list">
          {(loansQ.data || []).map((l) => (
            <LoanRow key={l.id} loan={l} canIssue={canIssue} />
          ))}
        </div>
      )}
    </div>
  );
}

function LoanRow({ loan, canIssue }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const isOverdue = !loan.returned_at && new Date(loan.due_at) < new Date();
  const doReturn = async () => {
    setBusy(true);
    try {
      const r = await LibraryAPI.returnLoan(loan.id);
      toast.success(
        r.fine_amount > 0
          ? `Returned. Late fine: ₹${r.fine_amount}`
          : "Returned"
      );
      qc.invalidateQueries({ queryKey: ["lib-loans"] });
      qc.invalidateQueries({ queryKey: ["lib-books"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const markPaid = async () => {
    try {
      await LibraryAPI.markPaid(loan.id);
      toast.success("Fine marked paid");
      qc.invalidateQueries({ queryKey: ["lib-loans"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3" data-testid={`loan-row-${loan.id}`}>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-sm truncate">{loan.book_title}</div>
        <div className="text-xs text-muted-foreground truncate">
          {loan.borrower_name} · {loan.borrower_type} · issued {new Date(loan.issued_at).toLocaleDateString()}
        </div>
      </div>
      <div className="text-xs text-right">
        <div className={isOverdue ? "text-destructive font-medium" : "text-muted-foreground"}>
          Due {new Date(loan.due_at).toLocaleDateString()}
        </div>
        {loan.fine_amount > 0 && (
          <div className="font-mono mt-0.5">
            <Badge variant={loan.fine_paid ? "outline" : "destructive"} className="text-[10px]">
              ₹{loan.fine_amount} {loan.fine_paid ? "paid" : "due"}
            </Badge>
          </div>
        )}
      </div>
      {canIssue && !loan.returned_at && (
        <Button size="sm" variant="outline" disabled={busy} onClick={doReturn} data-testid={`loan-return-${loan.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
          Return
        </Button>
      )}
      {canIssue && loan.fine_amount > 0 && !loan.fine_paid && (
        <Button size="sm" variant="ghost" onClick={markPaid} data-testid={`loan-mark-paid-${loan.id}`}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Mark paid
        </Button>
      )}
    </div>
  );
}

function AddBookDialog({ institutionId }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [isbn, setIsbn] = useState("");
  const [category, setCategory] = useState("");
  const [copies, setCopies] = useState(1);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    setBusy(true);
    try {
      await LibraryAPI.createBook({
        institution_id: institutionId,
        title: title.trim(),
        author: author.trim(),
        isbn: isbn.trim() || undefined,
        category: category.trim() || undefined,
        total_copies: Math.max(1, parseInt(copies, 10) || 1),
      });
      toast.success("Book added to catalogue");
      setTitle(""); setAuthor(""); setIsbn(""); setCategory(""); setCopies(1);
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["lib-books"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-book-btn"><Plus className="h-4 w-4 mr-1.5" /> Add book</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="add-book-dialog">
        <DialogHeader>
          <DialogTitle>Add book to catalogue</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="book-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Author</Label>
              <Input value={author} onChange={(e) => setAuthor(e.target.value)} data-testid="book-author-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Fiction, Science…" data-testid="book-category-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>ISBN</Label>
              <Input value={isbn} onChange={(e) => setIsbn(e.target.value)} maxLength={32} data-testid="book-isbn-input" />
            </div>
            <div>
              <Label>Copies</Label>
              <Input type="number" min={1} max={10000} value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="book-copies-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="book-submit">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Add book</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IssueDialog({ book, onClose }) {
  const [borrowerType, setBorrowerType] = useState("student");
  const [borrowerId, setBorrowerId] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  const studentsQ = useQuery({
    queryKey: ["lib-students-pick", book.institution_id, q],
    queryFn: () => StudentsAPI.list({ institution_id: book.institution_id, q: q || undefined }),
    enabled: borrowerType === "student",
  });
  const staffQ = useQuery({
    queryKey: ["lib-staff-pick", book.institution_id, q],
    queryFn: () => StaffAPI.list({ institution_id: book.institution_id, q: q || undefined }),
    enabled: borrowerType === "staff",
  });
  const list = (borrowerType === "student" ? studentsQ.data : staffQ.data) || [];

  const submit = async () => {
    if (!borrowerId) return toast.error("Pick a borrower");
    setBusy(true);
    try {
      await LibraryAPI.issue({ book_id: book.id, borrower_type: borrowerType, borrower_id: borrowerId });
      toast.success("Book issued");
      onClose();
      qc.invalidateQueries({ queryKey: ["lib-books"] });
      qc.invalidateQueries({ queryKey: ["lib-loans"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="issue-dialog">
        <DialogHeader>
          <DialogTitle>Issue &ldquo;{book.title}&rdquo;</DialogTitle>
          <DialogDescription>{book.available_copies} of {book.total_copies} copies available.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Borrower type</Label>
              <Select value={borrowerType} onValueChange={(v) => { setBorrowerType(v); setBorrowerId(""); }}>
                <SelectTrigger data-testid="borrower-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="staff">Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Search</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or admission no…" data-testid="borrower-search" />
            </div>
          </div>
          <div className="bg-card rounded-xl shadow-sm max-h-56 overflow-auto divide-y divide-border" data-testid="borrower-list">
            {list.length === 0 ? (
              <div className="p-4 text-center text-xs text-muted-foreground">No results</div>
            ) : (
              list.map((b) => {
                const name = (b.first_name || b.last_name) ? `${b.first_name||""} ${b.last_name||""}`.trim() : (b.name || b.email);
                return (
                  <label key={b.id} className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-secondary/40 ${borrowerId === b.id ? "bg-primary/10" : ""}`} data-testid={`borrower-${b.id}`}>
                    <input type="radio" name="borrower" checked={borrowerId === b.id} onChange={() => setBorrowerId(b.id)} />
                    <div className="min-w-0 text-sm">
                      <div className="truncate">{name}</div>
                      <div className="text-[11px] font-mono text-muted-foreground truncate">{b.admission_no || b.email}</div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !borrowerId} data-testid="issue-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettingsDialog({ institutionId }) {
  const [open, setOpen] = useState(false);
  const settingsQ = useQuery({
    queryKey: ["lib-settings", institutionId],
    queryFn: () => LibraryAPI.settings(institutionId),
    enabled: open,
  });
  const [loanDays, setLoanDays] = useState(14);
  const [fine, setFine] = useState(5);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (settingsQ.data) {
      setLoanDays(settingsQ.data.loan_days);
      setFine(settingsQ.data.fine_per_day);
    }
  }, [settingsQ.data]);

  const save = async () => {
    setBusy(true);
    try {
      await LibraryAPI.saveSettings({ institution_id: institutionId, loan_days: parseInt(loanDays, 10), fine_per_day: parseFloat(fine) });
      toast.success("Library settings saved");
      qc.invalidateQueries({ queryKey: ["lib-settings"] });
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" data-testid="lib-settings-btn"><SettingsIcon className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="lib-settings-dialog">
        <DialogHeader>
          <DialogTitle>Library settings</DialogTitle>
          <DialogDescription>Loan period and fine per overdue day for this institution.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Loan days</Label>
            <Input type="number" min={1} max={365} value={loanDays} onChange={(e) => setLoanDays(e.target.value)} data-testid="settings-loan-days" />
          </div>
          <div>
            <Label>Fine / day (₹)</Label>
            <Input type="number" min={0} step="0.5" value={fine} onChange={(e) => setFine(e.target.value)} data-testid="settings-fine" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="settings-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
